import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, MousePointerClick, Eye, TrendingUp, ShoppingBag,
  Activity, UserPlus, Globe, Smartphone, Download, RefreshCw,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid,
} from "recharts";

const RANGE_OPTIONS = [
  { label: "Today", value: 1 },
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

const USER_TYPES = [
  { label: "All", value: "all" },
  { label: "Guests", value: "guest" },
  { label: "Registered", value: "registered" },
];

const KpiCard = ({ icon, label, value, hint, testId }) => (
  <div className="card-premium p-5" data-testid={testId}>
    <div className="inline-grid place-items-center h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
      {icon}
    </div>
    <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
    <div className="mono text-2xl font-bold mt-1">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </div>
);

const FunnelBar = ({ label, value, max, testId }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">{label}</span>
        <span className="mono text-muted-foreground">{value.toLocaleString()} ({pct}%)</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const EVENT_TO_LABEL = {
  PAGE_VIEW: "Viewed a page",
  PRODUCT_VIEW: "Viewed a product",
  BUY_BUTTON_CLICK: "Clicked Buy Now",
  AFFILIATE_LINK_CLICK: "Clicked affiliate link",
  SUSTAINABILITY_SCORE_VIEW: "Viewed sustainability score",
  SUSTAINABILITY_BREAKDOWN_VIEW: "Opened score breakdown",
  SIGNUP_PAGE_VIEW: "Visited signup page",
  SIGNUP_STARTED: "Started signup",
  SIGNUP_COMPLETED: "Created account",
  LOGIN: "Logged in",
  LOGOUT: "Logged out",
  STITCH: "Linked account",
  GREENCOIN_MILESTONE_REACHED: "Hit GreenCoin milestone",
  SCRATCH_CARD_UNLOCKED: "Unlocked scratch card",
  BRAND_WAITLIST_SUBMITTED: "Applied to brand waitlist",
};

const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (name, rows) => {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => csvEscape(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const AnalyticsTab = () => {
  const [days, setDays] = useState(7);
  const [userType, setUserType] = useState("all");
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, r, p, u] = await Promise.all([
        api.get("/analytics/summary", { params: { days, user_type: userType } }),
        api.get("/analytics/recent", { params: { limit: 50 } }),
        api.get("/analytics/products", { params: { days } }),
        api.get("/analytics/users", { params: { days, user_type: userType } }),
      ]);
      setSummary(s.data); setRecent(r.data); setProducts(p.data); setUsers(u.data);
    } catch (e) { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [days, userType]);

  const openUser = async (u) => {
    setSelectedUser(u);
    try {
      const { data } = await api.get(`/analytics/user/${u.id}/timeline`, { params: { limit: 100 } });
      setTimeline(data);
    } catch { setTimeline([]); }
  };

  const totals = summary?.kpis || {};
  const funnel = summary?.funnel || {};
  const funnelMax = Math.max(1, funnel.page_views || 0, totals.unique_visitors || 0);
  const eventsByName = summary?.events_by_name || {};

  return (
    <div className="space-y-6" data-testid="analytics-tab-content">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`text-xs px-3 py-1.5 rounded-full ${days === r.value ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              data-testid={`analytics-range-${r.value}`}
            >{r.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-4">
          {USER_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => setUserType(t.value)}
              className={`text-xs px-3 py-1.5 rounded-full ${userType === t.value ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              data-testid={`analytics-type-${t.value}`}
            >{t.label}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={fetchAll} className="text-xs px-3 py-1.5 rounded-full bg-muted flex items-center gap-1" data-testid="analytics-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => downloadCsv(`analytics-users-${days}d.csv`, users)}
            className="text-xs px-3 py-1.5 rounded-full bg-muted flex items-center gap-1"
            data-testid="analytics-export"
          >
            <Download className="h-3.5 w-3.5" /> Export users
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="analytics-kpis">
        <KpiCard icon={<Users className="h-4 w-4" />} label="Visitors" value={totals.unique_visitors ?? 0}
                 hint={`${totals.guest_visitors ?? 0} guests`} testId="kpi-visitors" />
        <KpiCard icon={<Activity className="h-4 w-4" />} label="Sessions" value={totals.sessions ?? 0}
                 hint={`${totals.total_events ?? 0} events`} testId="kpi-sessions" />
        <KpiCard icon={<UserPlus className="h-4 w-4" />} label="Signups" value={eventsByName.SIGNUP_COMPLETED ?? 0}
                 hint={`${eventsByName.SIGNUP_STARTED ?? 0} started`} testId="kpi-signups" />
        <KpiCard icon={<Eye className="h-4 w-4" />} label="Product Views" value={eventsByName.PRODUCT_VIEW ?? 0} testId="kpi-productviews" />
        <KpiCard icon={<ShoppingBag className="h-4 w-4" />} label="Buy Clicks" value={(eventsByName.BUY_BUTTON_CLICK ?? 0) + (eventsByName.AFFILIATE_LINK_CLICK ?? 0)} testId="kpi-buyclicks" />
      </div>

      {/* Traffic chart */}
      <div className="card-premium p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Visitors over time</h3>
          <span className="text-xs text-muted-foreground">Last {days} days</span>
        </div>
        {summary?.timeseries?.length ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary.timeseries}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RTooltip />
                <Line type="monotone" dataKey="visitors" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="events" stroke="#f59e0b" strokeWidth={2} dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-8 text-center">No traffic in this window yet.</p>
        )}
      </div>

      {/* Funnel + composition */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-premium p-5 space-y-3" data-testid="analytics-funnel">
          <h3 className="font-semibold mb-2">Customer funnel</h3>
          <FunnelBar label="Page views" value={funnel.page_views || 0} max={funnelMax} testId="funnel-pv" />
          <FunnelBar label="Product views" value={funnel.product_views || 0} max={funnelMax} testId="funnel-prod" />
          <FunnelBar label="Score interactions" value={funnel.score_views || 0} max={funnelMax} testId="funnel-score" />
          <FunnelBar label="Buy clicks" value={funnel.buy_clicks || 0} max={funnelMax} testId="funnel-buy" />
          <FunnelBar label="Signups completed" value={funnel.signup_completed || 0} max={funnelMax} testId="funnel-signup" />
          <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-border text-xs">
            <div><div className="text-muted-foreground">Visitor → Product view</div><div className="mono font-bold">{summary?.conversion?.visitor_to_product_view ?? 0}%</div></div>
            <div><div className="text-muted-foreground">Product view → Buy click</div><div className="mono font-bold">{summary?.conversion?.product_view_to_buy_click ?? 0}%</div></div>
            <div><div className="text-muted-foreground">Visitor → Signup</div><div className="mono font-bold">{summary?.conversion?.visitor_to_signup ?? 0}%</div></div>
            <div><div className="text-muted-foreground">Signup completion</div><div className="mono font-bold">{summary?.conversion?.signup_completion_rate ?? 0}%</div></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-premium p-5" data-testid="analytics-composition">
            <h3 className="font-semibold mb-3">User composition</h3>
            {(() => {
              const total = totals.unique_visitors || 0;
              const guests = totals.guest_visitors || 0;
              const reg = Math.max(0, total - guests);
              const gPct = total ? Math.round((guests / total) * 100) : 0;
              const rPct = 100 - gPct;
              return (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span>Guests</span><span className="mono">{guests} ({gPct}%)</span></div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${gPct}%` }} /></div>
                  <div className="flex justify-between text-sm pt-2"><span>Registered</span><span className="mono">{reg} ({rPct}%)</span></div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${rPct}%` }} /></div>
                </div>
              );
            })()}
          </div>

          <div className="card-premium p-5" data-testid="analytics-devices">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Smartphone className="h-4 w-4" /> Devices</h3>
            {Object.keys(summary?.devices || {}).length ? (
              <div className="space-y-2 text-sm">
                {Object.entries(summary.devices).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="mono text-muted-foreground">{v}</span></div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No data yet.</p>}
          </div>

          <div className="card-premium p-5" data-testid="analytics-sources">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Globe className="h-4 w-4" /> Acquisition</h3>
            {summary?.sources?.length ? (
              <div className="space-y-2 text-sm">
                {summary.sources.slice(0, 6).map((s) => (
                  <div key={s.source} className="flex justify-between">
                    <span className="capitalize">{s.source}</span>
                    <span className="mono text-muted-foreground">{s.visitors} visitors</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">Add ?utm_source=… to your links to track sources.</p>}
          </div>
        </div>
      </div>

      {/* Product analytics */}
      <div className="card-premium overflow-hidden" data-testid="analytics-products-table">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold">Top products</h3>
          <button
            onClick={() => downloadCsv(`analytics-products-${days}d.csv`, products)}
            className="text-xs px-3 py-1 rounded-full bg-muted flex items-center gap-1"
            data-testid="export-products"
          ><Download className="h-3 w-3" /> CSV</button>
        </div>
        <div className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
          <div className="col-span-5">Product</div>
          <div className="col-span-1 text-center">Score</div>
          <div className="col-span-2 text-right">Views</div>
          <div className="col-span-2 text-right">Buy clicks</div>
          <div className="col-span-2 text-right">CTR</div>
        </div>
        {products.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No product engagement yet in this window.</p>}
        {products.slice(0, 15).map((p) => (
          <div key={p.product_id} className="grid grid-cols-12 gap-3 px-5 py-2 items-center border-b border-border/40 last:border-b-0 hover:bg-muted/30 text-sm" data-testid={`analytics-product-${p.product_id}`}>
            <div className="col-span-5 flex items-center gap-2 min-w-0">
              {p.image && <img src={p.image} alt="" className="h-8 w-8 rounded-lg object-cover" />}
              <div className="min-w-0">
                <div className="font-semibold truncate">{p.name || p.product_id}</div>
                <div className="text-[11px] text-muted-foreground truncate">{p.brand} · {p.category}</div>
              </div>
            </div>
            <div className="col-span-1 text-center mono">{p.sustainability_score ?? "—"}</div>
            <div className="col-span-2 text-right mono">{p.views} <span className="text-[10px] text-muted-foreground">({p.unique_viewers})</span></div>
            <div className="col-span-2 text-right mono">{p.buy_clicks}</div>
            <div className="col-span-2 text-right mono text-emerald-600">{p.ctr_pct}%</div>
          </div>
        ))}
      </div>

      {/* Users + recent activity */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-premium overflow-hidden" data-testid="analytics-users-table">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold">Users ({users.length})</h3>
            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {users.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No user activity yet.</p>}
            {users.map((u) => (
              <button
                key={u.id + u.type}
                onClick={() => openUser(u)}
                className="w-full text-left grid grid-cols-12 gap-2 px-5 py-2.5 border-b border-border/40 last:border-b-0 hover:bg-muted/30"
                data-testid={`user-row-${u.id}`}
              >
                <div className="col-span-6 min-w-0">
                  <div className="font-semibold truncate text-sm">{u.name || u.id}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {u.type === "Registered" ? u.email : "Anonymous session"}
                  </div>
                </div>
                <div className="col-span-2 text-xs text-muted-foreground text-right">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${u.type === "Registered" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>{u.type}</span>
                </div>
                <div className="col-span-2 text-right mono text-xs">{u.product_views} views</div>
                <div className="col-span-2 text-right mono text-xs">{u.buy_clicks} clicks</div>
              </button>
            ))}
          </div>
        </div>

        <div className="card-premium overflow-hidden" data-testid="analytics-recent">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold">Recent activity</h3>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {recent.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No events tracked yet.</p>}
            {recent.map((r) => {
              const who = r.user_name ? `${r.user_name}` : `Guest #${(r.anonymous_id || "").slice(0, 6).toUpperCase()}`;
              return (
                <div key={r.id} className="px-5 py-2.5 border-b border-border/40 last:border-b-0 text-sm" data-testid={`event-${r.id}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold truncate">{who}</span>
                    <span className="mono text-[10px] text-muted-foreground shrink-0">{r.at?.slice(11, 19)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {EVENT_TO_LABEL[r.event_name] || r.event_name}
                    {r.product_name ? ` — “${r.product_name}”` : ""}
                    {r.path ? ` · ${r.path}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* User timeline dialog */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setSelectedUser(null)} data-testid="user-timeline-modal">
          <div onClick={(e) => e.stopPropagation()} className="card-premium max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">{selectedUser.name}</h3>
                <p className="text-xs text-muted-foreground">{selectedUser.email || "Anonymous"}</p>
              </div>
              <button onClick={() => setSelectedUser(null)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="close-timeline">Close</button>
            </div>
            <div className="space-y-3">
              {timeline.length === 0 && <p className="text-sm text-muted-foreground">No events for this user.</p>}
              {timeline.map((e) => (
                <div key={e.id} className="flex gap-3">
                  <div className="mono text-[10px] text-muted-foreground w-20 shrink-0 pt-0.5">{e.at?.slice(5, 16).replace("T", " ")}</div>
                  <div className="text-sm">
                    <div className="font-semibold">{EVENT_TO_LABEL[e.event_name] || e.event_name}</div>
                    {e.product_name && <div className="text-xs text-muted-foreground">Product: {e.product_name}</div>}
                    {e.path && <div className="text-xs text-muted-foreground">Path: {e.path}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
